<template>
	<div
		class="hdl-single-choice question"
		role="radiogroup"
		:aria-labelledby="`${question.uuid}-label`"
		:id="`${question.uuid}-container`"
		:class="{
			'highlight-required': highlightRequired
		}"
		@keydown="keydownHandler"
	>
		<div
			ref="rendered"
			v-html="question.renderedQuestion"
		/>
		<div
			class="choices mb-2"
			ref="container"
		>
			<div
				v-for="choice, index of choices"
				class="choice"
				:class="{'selected': selectedChoice === choice.uuid, 'has-text-option': choice.hasTextOption}"
				@click.prevent.stop="selectChoice(choice.uuid)"
				role="radio"
				:aria-checked="selectedChoice === choice.uuid"
				:aria-labelledby="`${choice.uuid}-label`"
				ref="choices"
				:data-uuid="choice.uuid"
				:data-index="choice.index"
				tabindex=0
			>
				<div
					class="choice-indicator mr-2 flex-none"
					v-if="hasChoicesBadge"
				>
					{{ indexToBadge(index) }}
				</div>
				<div
					class="mr-2 flex-none flex flex-row items-center radio-container"
					v-else
				>
					<radio-indicator :selected="selectedChoice === choice.uuid" />
				</div>
				<div
					class="flex flex-col flex-1"
					:id="`${choice.uuid}-label`"
				>
					<div class="choice-label">{{ choice.label }}</div>
					<hdl-textbox
						class="w-full"
						:model-value="choicesTextMap[choice.uuid]"
						@update:model-value="handleChoiceTextboxInput(choice, $event)"
						@key-up-out="handleChoiceTextboxMovement(choice, 'up')"
						@key-down-out="handleChoiceTextboxMovement(choice, 'down')"
						@escape="handleChoiceTextboxMovement(choice, 'escape')"
						@enter="handleChoiceTextboxMovement(choice,'enter')"
						@click.stop="() => {}"
						capture-keys
						v-if="choice.hasTextOption"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { PublishedQuestionChoiceType, PublishedSurveyQuestionType, QuestionChoiceBadge, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { inPlaceShuffleArray, indexToAlpha } from '@/common/utils';

@Component({
	emits: ['intentionalBlur']
})
export default class SingleChoice extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: Array<string> | null;

	selectedChoice: string | null = null;

	highlightRequired: boolean = false; /* Visually accentuate the fact that this question is required */

	choicesTextMap: {[choiceUuid: string]: string | null} = {};

	mounted(): void {
		this.$el.__vue__ = this;

		this.choicesTextMap = {};

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			if (existingResponse && existingResponse.choices && existingResponse.choices.length) {
				this.selectedChoice = existingResponse.choices[0].choiceUuid;
				this.choicesTextMap[existingResponse.choices[0].choiceUuid] = (existingResponse.choices[0].text)? existingResponse.choices[0].text : null;
			} else {
				this.selectedChoice = null;
			}

		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const choices = this.sortedChoiceEls;

		if (choices === undefined) {
			return true;
		}

		const selectedIndex = (this.selectedChoice !== null)?
				this.choices.findIndex(
					(c: PublishedQuestionChoiceType) => c.uuid === this.selectedChoice
				)
			:
				-1;

		if (this.selectedChoice === null || selectedIndex < 0 || selectedIndex > this.choices.length - 1) {
			if (fromDirection === 'below' && choices.length > 0) {
				choices[this.choices.length - 1].focus();
			} else if (choices.length > 0) {
				choices[0].focus();
			}
		} else {
			choices[selectedIndex].focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): void {
		const focusedUuid = this.focusedDomChoiceUuid();

		const focusedIndex = (focusedUuid !== null)?
				this.choices.findIndex(
					(c: PublishedQuestionChoiceType) => c.uuid === focusedUuid
				)
			:
				null;

		if (event.key === 'ArrowDown') {
			event.stopPropagation();
			event.preventDefault();

			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex < this.choices.length - 1) {
				const nextChoice = this.sortedChoiceEls[currentIndex + 1];

				if (nextChoice) {
					nextChoice.focus();
				}
			} else if (currentIndex === this.choices.length - 1) {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			}
		} else if (event.key === 'ArrowUp') {
			event.stopPropagation();
			event.preventDefault();

			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex > 0) {
				const nextChoice = this.sortedChoiceEls[currentIndex - 1];
				if (nextChoice) {
					nextChoice.focus();
				}
			} else if (currentIndex === 0) {
				this.$emit('intentionalBlur', this.$el, this.question, 'up');
			}
		} else if (event.key === 'ArrowRight') {
			const currentIndex = focusedIndex;

			if (currentIndex !== null && currentIndex > -1) {
				const choiceEl = this.sortedChoiceEls[currentIndex];
				const inputEl = choiceEl.querySelector('input[type="text"]') as HTMLInputElement;

				if (inputEl) {
					inputEl.focus();
				}
			}
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			event.preventDefault();

			if (focusedUuid !== null) {
				this.selectChoice(focusedUuid);
			}

			if (event.key === 'Enter') {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			} else if (event.key === ' ') {
				if (focusedIndex !== null && focusedIndex > -1 && this.selectedChoice !== null) {
					const inputEl = this.sortedChoiceEls[focusedIndex].querySelector('input[type="text"]') as HTMLInputElement;

					if (inputEl) {
						inputEl.focus();
					}
				}
			}
		}
	}

	handleChoiceTextboxInput(choice: PublishedQuestionChoiceType, newValue: string): void {
		this.choicesTextMap[choice.uuid] = (newValue.trim().length > 0)? newValue : null;

		if (this.responseGroup) {
			this.selectedChoice = choice.uuid;
			this.responseGroup.setResponse(
				this.question.uuid,
				null,
				null,
				[{ choiceUuid: choice.uuid, text: newValue }]
			);
		}
	}

	handleChoiceTextboxMovement(choice: PublishedQuestionChoiceType, movement: 'up' | 'down' | 'escape' | 'enter') {
		if (movement === 'up') {
			const choiceIndex = this.choices.findIndex((c: PublishedQuestionChoiceType) => c.uuid === choice.uuid);

			const targetChoiceIndex = (choiceIndex < 0)? 0 : choiceIndex - 1;

			if (targetChoiceIndex <= 0) {
				this.$emit('intentionalBlur', this.$el, this.question, 'up');
			} else {
				const nextChoice = this.sortedChoiceEls[targetChoiceIndex];

				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (movement === 'down') {
			const choiceIndex = this.choices.findIndex((c: PublishedQuestionChoiceType) => c.uuid === choice.uuid);

			const targetChoiceIndex = (choiceIndex < 0)? 0 : choiceIndex + 1;

			if (targetChoiceIndex >= this.choices.length - 1) {
				this.$emit('intentionalBlur', this.$el, this.question, 'down');
			} else {
				const nextChoice = this.sortedChoiceEls[targetChoiceIndex];

				if (nextChoice) {
					nextChoice.focus();
				}
			}
		} else if (movement === 'escape') {

		} else if (movement === 'enter') {
			this.selectChoice(choice.uuid);
			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}
	}

	focusedDomChoiceUuid(): string | null {
		const container = this.$refs.container as HTMLDivElement | undefined;

		if (container === undefined) {
			return null;
		}

		if (document.activeElement && container.contains(document.activeElement)) {
			return document.activeElement.getAttribute('data-uuid');
		}

		return null;
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const requiredIndicator = document.createElement('div');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	selectChoice(choiceUuid: string | null): void {
		this.highlightRequired = false;

		if (this.selectedChoice === choiceUuid) {
			this.selectedChoice = null;
		} else {
			this.selectedChoice = choiceUuid;
		}

		if (this.responseGroup) {
			this.responseGroup.setResponse(
				this.question.uuid,
				null,
				null,
				(this.selectedChoice)?
						[{ choiceUuid: this.selectedChoice }]
					: null
			);
		}
	}

	getResponse(): Array<string> | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	/*input(event: Event): void {
		this.response = (event.target as HTMLTextAreaElement).value;

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, this.response, null);
		}
	}*/

	indexToBadge(index: number): string {
		if (this.question === null) {
			return '';
		}

		switch (this.question.choiceBadge) {
			case QuestionChoiceBadge.NONE:
				return '';
			break;
			case QuestionChoiceBadge.LETTERS:
				return indexToAlpha(index);
			break;
			case QuestionChoiceBadge.NUMBERS:
				return (index + 1).toString();
			break;
		}

		return '';
	}

	markError(error: SurveyResponseInputErrorType): void {
		if (error.code === SurveyResponseErrorCode.MISSING_REQUIRED) {
			this.highlightRequired = true;
		}
	}

	get sortedChoiceEls(): Array<HTMLDivElement> {
		return (this.$refs.choices as Array<HTMLDivElement>).sort(
			//@ts-ignore
			(a, b) => parseInt(a.getAttribute('data-index')) - parseInt(b.getAttribute('data-index'))
		);
	}

	get choices(): Array<PublishedQuestionChoiceType> {
		if (this.question === null) {
			return [];
		}

		if (!this.question.randomizeChoices) {
			return this.question.choices;
		}

		const shuffled = [...this.question.choices];
		inPlaceShuffleArray<PublishedQuestionChoiceType>(shuffled);
		return shuffled;
	}

	get hasChoicesBadge(): boolean {
		if (this.question === null) {
			return false;
		}

		return this.question.choiceBadge !== QuestionChoiceBadge.NONE;
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}
}
</script>

<style lang="scss">
.hdl-single-choice {
	@apply relative;

	&.highlight-required {
		label {
			@apply text-hdl-red;
		}
	}

	.choices {
		.choice {
			@apply flex-row items-start px-2 rounded flex mb-2 cursor-pointer select-none p-1;

			min-width: 100%;
			width: 100%;

			&:hover {
				@apply bg-fill-hover;
			}

			&.has-text-option {
				.choice-label {
					@apply pb-1;
				}
			}

			&.selected {
				border-color: var(--main);

				.choice-indicator {
					background: var(--main);
				}
			}

			min-height: 32px;

			&:focus {
				.choice-indicator,
				.radio-indicator {
					@apply border-accent-border;
				}
				outline: none;
			}

			.radio-container {
				padding-top: 1px;
			}

			.choice-indicator {
				@apply rounded flex flex-row items-center justify-center border border-transparent text-center;
				padding-top: 1px;
				font-weight: 600;
				background: rgba(0, 0, 0, .25);
				color: #F8F8F8;
				width: 22px;
			}
		}
	}
}
</style>
